<template>
  <el-dialog
    v-model="visible"
    title="请使用手机扫码完成实人认证"
    width="420px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    destroy-on-close
    @opened="onOpened"
    @closed="onClosed"
  >
    <div class="qr-wrap">
      <canvas ref="canvasRef" class="qr-canvas" />
      <p class="tip">
        {{
          verifyMode === 'viapi_liveness'
            ? '请使用手机扫描二维码，完成自拍人照比对后返回本页'
            : '请使用手机扫描二维码，完成炫光活体认证后返回本页'
        }}
      </p>
      <p class="expire">二维码有效期 {{ remainSec }} 秒</p>
      <p v-if="statusText" class="status">{{ statusText }}</p>
    </div>
    <template #footer>
      <el-button @click="onCancel">取消</el-button>
      <el-button type="primary" :loading="refreshing" @click="refreshQr">刷新二维码</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'
import { ElMessage } from 'element-plus'
import QRCode from 'qrcode'
import { createLivenessQr, getLivenessTicketStatus } from '@/api/agreement'

const props = defineProps<{
  modelValue: boolean
  templateId?: string | number
  verifyMode?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void
  (e: 'passed', payload: { certifyId: string; ticketId: string }): void
  (e: 'cancel'): void
}>()

const visible = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
})

const canvasRef = ref<HTMLCanvasElement | null>(null)
const ticketId = ref('')
const qrUrl = ref('')
const remainSec = ref(300)
const statusText = ref('')
const refreshing = ref(false)
let pollTimer: ReturnType<typeof setInterval> | null = null
let countdownTimer: ReturnType<typeof setInterval> | null = null
/** 区分「取消关闭」与「认证成功关闭」，避免 success 后再触发 cancel */
let closedBySuccess = false

/** 手机要能访问的学生端地址（localhost 手机打不开） */
const resolveMobileOrigin = () => {
  const configured = (import.meta.env.VITE_H5_PUBLIC_ORIGIN || '').trim().replace(/\/$/, '')
  if (configured) return configured

  const { protocol, hostname, port } = window.location
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const lanHost = (import.meta.env.VITE_DEV_LAN_HOST || '').trim()
    if (lanHost) {
      const host = lanHost.replace(/^https?:\/\//, '').replace(/\/$/, '')
      return `${protocol}//${host.includes(':') ? host : `${host}${port ? `:${port}` : ''}`}`
    }
  }
  return window.location.origin
}

const clearTimers = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

const drawQr = async (tid: string) => {
  await nextTick()
  const canvas = canvasRef.value
  if (!canvas) return
  const origin = resolveMobileOrigin()
  // 使用「路径 + 查询参数」，同时带 Hash 兜底（部分手机浏览器对 History 深链不友好）
  // index.html / main.ts / router 会保证最终落到 /agreement-liveness
  const url = `${origin}/agreement-liveness?ticketId=${encodeURIComponent(tid)}#/agreement-liveness?ticketId=${encodeURIComponent(tid)}`
  qrUrl.value = url
  if (/localhost|127\.0\.0\.1/i.test(origin)) {
    ElMessage.warning(
      '当前二维码指向 localhost，手机无法访问。请用局域网IP打开本站（如 http://192.168.x.x:3003），或在 .env.development 配置 VITE_DEV_LAN_HOST',
    )
  }
  await QRCode.toCanvas(canvas, url, {
    width: 220,
    margin: 2,
  })
}

const startCountdown = (seconds: number) => {
  remainSec.value = seconds
  if (countdownTimer) clearInterval(countdownTimer)
  countdownTimer = setInterval(() => {
    remainSec.value -= 1
    if (remainSec.value <= 0) {
      remainSec.value = 0
      statusText.value = '二维码已过期，请刷新'
      clearTimers()
    }
  }, 1000)
}

const startPoll = () => {
  if (pollTimer) clearInterval(pollTimer)
  pollTimer = setInterval(async () => {
    if (!ticketId.value) return
    try {
      const res: any = await getLivenessTicketStatus(ticketId.value)
      const status = res?.status
      if (status === 'AUTHING') {
        statusText.value = '手机端认证中…'
      } else if (status === 'SUCCESS' && res?.certifyId) {
        statusText.value = '认证成功'
        clearTimers()
        closedBySuccess = true
        emit('passed', { certifyId: res.certifyId, ticketId: ticketId.value })
        visible.value = false
      } else if (status === 'FAILED') {
        statusText.value = '认证失败，请刷新二维码后重试'
      } else if (status === 'EXPIRED') {
        statusText.value = '二维码已过期，请刷新'
        clearTimers()
      }
      if (typeof res?.expireRemainSec === 'number' && res.expireRemainSec >= 0) {
        remainSec.value = res.expireRemainSec
      }
    } catch (e) {
      console.error('轮询活体状态失败', e)
    }
  }, 2000)
}

const refreshQr = async () => {
  if (!props.templateId) {
    ElMessage.warning('协议模板缺失')
    return
  }
  refreshing.value = true
  clearTimers()
  statusText.value = ''
  try {
    const res: any = await createLivenessQr(props.templateId)
    ticketId.value = res?.ticketId || ''
    const expire = res?.expireSeconds || 300
    if (!ticketId.value) {
      ElMessage.error('创建扫码失败')
      return
    }
    await drawQr(ticketId.value)
    startCountdown(expire)
    startPoll()
  } catch (e) {
    console.error(e)
  } finally {
    refreshing.value = false
  }
}

const onCancel = () => {
  closedBySuccess = false
  visible.value = false
}

const onOpened = () => {
  closedBySuccess = false
  refreshQr()
}

const onClosed = () => {
  clearTimers()
  ticketId.value = ''
  statusText.value = ''
  if (!closedBySuccess) {
    emit('cancel')
  }
  closedBySuccess = false
}

onBeforeUnmount(() => {
  clearTimers()
})
</script>

<style scoped lang="scss">
.qr-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0 16px;
}
.qr-canvas {
  width: 220px;
  height: 220px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
}
.tip {
  margin-top: 16px;
  color: #606266;
  font-size: 14px;
  text-align: center;
  line-height: 1.6;
  padding: 0 12px;
}
.expire {
  margin-top: 8px;
  color: #909399;
  font-size: 13px;
}
.status {
  margin-top: 8px;
  color: #409eff;
  font-size: 14px;
}
</style>
